import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { localizedPath, type LocalizedPage } from '@lifetimeline/shared';

import { ToastProvider } from './components/Toast.js';
import { PageTracker } from './features/analytics/PageTracker.js';
import { AuthProvider } from './features/auth/AuthContext.js';
import { I18nProvider, useI18n } from './i18n/index.js';
import { LOCALIZED_ROUTES, LocalizedRoute } from './i18n/LocalizedRoute.js';
import { AnonymousPage } from './routes/AnonymousPage.js';
import { AnalyticsPage } from './routes/AnalyticsPage.js';
import { ConnectionsPage } from './routes/ConnectionsPage.js';
import { LandingPage } from './routes/LandingPage.js';
import { PeoplePage } from './routes/PeoplePage.js';
import { ProfilePage } from './routes/ProfilePage.js';
import { PrivacyPage } from './routes/PrivacyPage.js';
import { PublicConnectionsPage } from './routes/PublicConnectionsPage.js';
import { PublicProfilePage } from './routes/PublicProfilePage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import { SignInPage } from './routes/SignInPage.js';
import { SignUpPage } from './routes/SignUpPage.js';

/**
 * 라우팅.
 *
 * `@`가 사용자 네임스페이스를 가른다 — `/@junho`는 사용자, `/settings`는 앱 화면.
 * 접두사가 없으면 앱 경로를 추가할 때마다 그 단어를 userId 예약어로 막아야 하고,
 * 이미 그 id를 쓰는 사람이 있으면 손쓸 수 없다(계획 §2).
 *
 * ⚠️ 이 경로들이 **직접 진입**으로도 열려야 한다. SPA는 링크 클릭으로는 되고
 * URL 붙여넣기로는 깨지기 쉬운데, 이 서비스는 공유가 핵심이라 치명적이다.
 * CloudFront가 404를 index.html로 돌려주도록 설정해 뒀다(infra/web.yaml).
 */
export function App() {
  return (
    <BrowserRouter>
      {/* 화면은 안 그린다 — 주소가 바뀔 때 정본 주소와 방문 기록만 챙긴다 */}
      <PageTracker />
      <I18nProvider>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              {/*
                홈은 로그인 여부와 상관없이 **소개 화면**이다. 내 것을 적고
                고치는 자리는 내 프로필(`/me`)이다 — 홈이 사람마다 다른 화면이
                되면 "life-timeline.com이 뭐 하는 곳인지"를 보여줄 자리가 없어진다.
              */}
              {/*
                홈과 개인정보처리방침은 **언어마다 주소가 따로** 있다(`/en`, `/ko/privacy`).
                검색엔진이 언어판을 각각 다른 문서로 색인하게 하려는 것이다 —
                한 주소가 보는 사람마다 다른 언어로 뜨면 검색엔진은 한쪽만 안다.

                접두어 없는 주소(`/`, `/privacy`)는 배포에서는 CloudFront가 먼저
                보낸다(`infra/web.yaml`). 여기 있는 것은 개발 서버와 앱 안의 옛 링크용이다.
              */}
              {LOCALIZED_ROUTES.map(({ language, page, path }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <LocalizedRoute language={language} page={page}>
                      {page === '' ? <LandingPage /> : <PrivacyPage />}
                    </LocalizedRoute>
                  }
                />
              ))}
              <Route path="/" element={<ToLocalized page="" />} />
              <Route path="/anonymous" element={<AnonymousPage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/me" element={<ProfilePage />} />
              <Route path="/me/connections" element={<ConnectionsPage />} />
              <Route path="/me/analytics" element={<AnalyticsPage />} />
              {/* 예전 주소. 탭이 된 뒤에도 나눠 준 링크가 살아 있어야 한다 */}
              <Route path="/me/picks" element={<Navigate to="/me?tab=picks" replace />} />
              <Route path="/people" element={<PeoplePage />} />
              {/* 예전 주소. 분석 탭이 된 뒤에도 나눠 준 링크가 살아 있어야 한다 */}
              <Route path="/me/viewers" element={<Navigate to="/me/analytics" replace />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* 구글 OAuth 앱을 게시하려면 이 주소가 있어야 한다 */}
              <Route path="/privacy" element={<ToLocalized page="/privacy" />} />
              {/*
                ⚠️ `/@:handle`로 쓸 수 없다. React Router는 한 세그먼트 안에
                리터럴과 파라미터를 섞는 것을 지원하지 않는다(`@:handle`은 매칭
                안 됨). `/:handle`로 받고 `@` 판별은 화면이 한다.

                위의 정적 경로들이 먼저 매칭되므로 `/settings`가 사용자 이름으로
                오인되지 않는다.
              */}
              <Route path="/:handle" element={<PublicProfilePage />} />
              {/* 남의 인연 — 프로필의 인연 수를 누르면 온다 */}
              <Route path="/:handle/connections" element={<PublicConnectionsPage />} />
              {/* 모르는 경로는 홈으로. 404 화면을 따로 두지 않는다 —
                  잘못된 링크로 들어온 사람에게 막다른 길을 주지 않는다 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

/**
 * 접두어 없는 주소를 **지금 언어의** 언어판으로 보낸다.
 *
 * 배포에서는 여기까지 오기 전에 CloudFront가 쿠키를 보고 보낸다. 앱 안에서
 * 옛 링크(`/`)를 누른 경우엔 이미 정해진 언어를 따른다.
 */
function ToLocalized({ page }: { page: LocalizedPage }) {
  const { language } = useI18n();
  return <Navigate to={localizedPath(language, page)} replace />;
}
